import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { staffAPI, shiftAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  Clock, 
  Calendar, 
  Plus, 
  Edit, 
  Trash2, 
  User,
  Copy,
  CheckCircle,
  XCircle,
  Coffee
} from 'lucide-react';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Lunedì', short: 'Lun' },
  { value: 1, label: 'Martedì', short: 'Mar' },
  { value: 2, label: 'Mercoledì', short: 'Mer' },
  { value: 3, label: 'Giovedì', short: 'Gio' },
  { value: 4, label: 'Venerdì', short: 'Ven' },
  { value: 5, label: 'Sabato', short: 'Sab' },
  { value: 6, label: 'Domenica', short: 'Dom' }
];

const TIME_SLOTS = [];
for (let h = 6; h <= 22; h++) {
  TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:00`);
  TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:30`);
}

export default function StaffScheduleManager() {
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [shiftForm, setShiftForm] = useState({
    day_of_week: 0,
    start_time: '09:00',
    end_time: '18:00',
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedStaff) {
      loadShifts(selectedStaff.id);
    }
  }, [selectedStaff]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await staffAPI.list();
      setStaff(res.data || []);
      if (res.data?.length > 0) {
        setSelectedStaff(res.data[0]);
      }
    } catch (error) {
      console.error('Error loading staff:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const loadShifts = async (staffId) => {
    try {
      const res = await shiftAPI.list(staffId);
      setShifts(res.data || []);
    } catch (error) {
      setShifts([]);
    }
  };

  const handleSaveShift = async () => {
    if (!selectedStaff) return;
    
    try {
      if (editingShift) {
        await shiftAPI.update(editingShift.id, {
          ...shiftForm,
          staff_id: selectedStaff.id
        });
        toast.success('Turno aggiornato');
      } else {
        await shiftAPI.create({
          ...shiftForm,
          staff_id: selectedStaff.id
        });
        toast.success('Turno creato');
      }
      setDialogOpen(false);
      setEditingShift(null);
      loadShifts(selectedStaff.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (!confirm('Sei sicuro di voler eliminare questo turno?')) return;
    try {
      await shiftAPI.delete(shiftId);
      toast.success('Turno eliminato');
      loadShifts(selectedStaff.id);
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleCopyWeek = async () => {
    if (!selectedStaff || shifts.length === 0) {
      toast.error('Nessun turno da copiare');
      return;
    }
    
    // Copy current staff shifts to all other staff
    try {
      for (const otherStaff of staff.filter(s => s.id !== selectedStaff.id)) {
        for (const shift of shifts) {
          await shiftAPI.create({
            staff_id: otherStaff.id,
            day_of_week: shift.day_of_week,
            start_time: shift.start_time,
            end_time: shift.end_time,
            is_active: shift.is_active
          });
        }
      }
      toast.success('Turni copiati a tutto lo staff');
    } catch (error) {
      toast.error('Errore nella copia');
    }
  };

  const openEditDialog = (shift) => {
    setEditingShift(shift);
    setShiftForm({
      day_of_week: shift.day_of_week,
      start_time: shift.start_time,
      end_time: shift.end_time,
      is_active: shift.is_active
    });
    setDialogOpen(true);
  };

  const openNewDialog = (dayOfWeek = 0) => {
    setEditingShift(null);
    setShiftForm({
      day_of_week: dayOfWeek,
      start_time: '09:00',
      end_time: '18:00',
      is_active: true
    });
    setDialogOpen(true);
  };

  const getShiftForDay = (dayValue) => {
    return shifts.find(s => s.day_of_week === dayValue);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="w-6 h-6 text-primary" />
            Gestione Turni
          </h2>
          <p className="text-muted-foreground">Configura gli orari di lavoro dello staff</p>
        </div>
        
        {selectedStaff && shifts.length > 0 && (
          <Button variant="outline" onClick={handleCopyWeek}>
            <Copy className="w-4 h-4 mr-2" />
            Copia a tutto lo staff
          </Button>
        )}
      </div>

      {/* Staff Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Seleziona Operatore</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {staff.map((member) => (
              <Button
                key={member.id}
                variant={selectedStaff?.id === member.id ? 'default' : 'outline'}
                onClick={() => setSelectedStaff(member)}
                className="gap-2"
              >
                <User className="w-4 h-4" />
                {member.user?.name || 'Operatore'}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Weekly Schedule */}
      {selectedStaff && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Orari Settimanali - {selectedStaff.user?.name}
              </CardTitle>
              <CardDescription>Clicca su un giorno per aggiungere o modificare il turno</CardDescription>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => openNewDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Turno
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editingShift ? 'Modifica Turno' : 'Nuovo Turno'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Giorno</Label>
                    <Select
                      value={shiftForm.day_of_week.toString()}
                      onValueChange={(v) => setShiftForm({ ...shiftForm, day_of_week: parseInt(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_WEEK.map((day) => (
                          <SelectItem key={day.value} value={day.value.toString()}>
                            {day.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Ora Inizio</Label>
                      <Select
                        value={shiftForm.start_time}
                        onValueChange={(v) => setShiftForm({ ...shiftForm, start_time: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_SLOTS.map((time) => (
                            <SelectItem key={time} value={time}>{time}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Ora Fine</Label>
                      <Select
                        value={shiftForm.end_time}
                        onValueChange={(v) => setShiftForm({ ...shiftForm, end_time: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_SLOTS.map((time) => (
                            <SelectItem key={time} value={time}>{time}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label>Turno Attivo</Label>
                    <Switch
                      checked={shiftForm.is_active}
                      onCheckedChange={(v) => setShiftForm({ ...shiftForm, is_active: v })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button onClick={handleSaveShift}>
                    {editingShift ? 'Salva' : 'Crea'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {DAYS_OF_WEEK.map((day) => {
                const shift = getShiftForDay(day.value);
                return (
                  <div
                    key={day.value}
                    className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                      shift?.is_active
                        ? 'border-primary bg-primary/5 hover:bg-primary/10'
                        : shift
                        ? 'border-muted bg-muted/50'
                        : 'border-dashed border-muted-foreground/30 hover:border-primary/50'
                    }`}
                    onClick={() => shift ? openEditDialog(shift) : openNewDialog(day.value)}
                  >
                    <div className="text-center">
                      <p className="font-semibold text-sm">{day.short}</p>
                      {shift ? (
                        <div className="mt-2">
                          {shift.is_active ? (
                            <>
                              <Badge variant="outline" className="text-xs mb-1">
                                {shift.start_time}
                              </Badge>
                              <p className="text-xs text-muted-foreground">-</p>
                              <Badge variant="outline" className="text-xs">
                                {shift.end_time}
                              </Badge>
                            </>
                          ) : (
                            <div className="flex flex-col items-center gap-1 text-muted-foreground">
                              <Coffee className="w-4 h-4" />
                              <span className="text-xs">Riposo</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="mt-2 text-muted-foreground">
                          <Plus className="w-4 h-4 mx-auto" />
                          <p className="text-xs mt-1">Aggiungi</p>
                        </div>
                      )}
                    </div>
                    
                    {shift && (
                      <div className="flex justify-center gap-1 mt-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); openEditDialog(shift); }}
                          className="p-1 hover:bg-muted rounded"
                        >
                          <Edit className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteShift(shift.id); }}
                          className="p-1 hover:bg-destructive/20 rounded text-destructive"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-primary/20 border-2 border-primary" />
                <span>Attivo</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-muted border-2 border-muted-foreground/30" />
                <span>Riposo</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded border-2 border-dashed border-muted-foreground/30" />
                <span>Non configurato</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {staff.length === 0 && (
        <Card className="p-8 text-center">
          <User className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">Nessun operatore configurato</p>
          <p className="text-sm text-muted-foreground">Aggiungi operatori nella sezione Staff</p>
        </Card>
      )}
    </div>
  );
}
